import React, { useState } from "react";
import {
  Box,
  Card,
  IconButton,
  useTheme,
  Tooltip,
  CircularProgress,
  Chip,
} from "@mui/material";
import { observer } from "mobx-react";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import CopyAllIcon from "@mui/icons-material/CopyAll";

import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { OrderPaymentLinkDetails } from "../../../../../models/Order/OrderPaymentLinkDetails";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import dayjs from "dayjs";
import { useStores } from "../../../use-stores";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";

interface OrderPaymentLinksTableProps {
  paymentLinks?: OrderPaymentLinkDetails[] | null;
  onRefresh: (paymentLinkId: number) => Promise<void>;
  onCreatePaymentLink: () => void;
}

const OrderPaymentLinksTable: React.FC<OrderPaymentLinksTableProps> = ({
  paymentLinks,
  onRefresh,
  onCreatePaymentLink,
}) => {
  const theme = useTheme();
  const { userStore } = useStores();

  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [refreshingId, setRefreshingId] = useState<number | null>(null);

  const hasLinks = Array.isArray(paymentLinks) && paymentLinks.length > 0;
  const hasMaxLinks = Array.isArray(paymentLinks) && paymentLinks.length >= 10;

  const canRefresh = (lastRefreshAt?: string | null): boolean => {
    if (!lastRefreshAt) return true;
    const lastRefresh = dayjs(lastRefreshAt);
    const now = dayjs();
    const minutesSinceRefresh = now.diff(lastRefresh, "minute");
    return minutesSinceRefresh >= 1;
  };

  const getNextRefreshTime = (lastRefreshAt: string): string => {
    const nextRefreshTime = dayjs(lastRefreshAt).add(1, "minute");
    return nextRefreshTime.format("DD/MM/YYYY, HH:mm");
  };

  return (
    <Box sx={{ borderRadius: 2 }}>
      <Card
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: "12px",
          border: `1px solid ${theme.palette.customColors.lightGray}`,
          boxShadow: `0 2px 8px ${theme.palette.customColors.shadowLightBorder}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <CustomTypography variant="heading4" fontWeight={700}>
            Payment Links
          </CustomTypography>
          {userStore.hasPermission(EmployeeRoles.GENERATE_ORDER_PAYMENT_LINK) &&
            !hasMaxLinks && (
              <AppButton
                label="Create Payment Link"
                startIcon={<AddIcon />}
                onClick={onCreatePaymentLink}
                customStyles={{
                  backgroundColor: "transparent",
                  padding: 1,
                  margin: 0,
                  fontSize: "14px",
                  color: theme.palette.customColors.pinkDark,
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: theme.palette.customColors.pinkDark,
                    boxShadow: "none",
                  },
                }}
              />
            )}
        </Box>

        {!hasLinks ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <CustomTypography
              variant="body2"
              fontWeight={700}
              color="rgba(218, 218, 218, 1)"
            >
              NOTHING FOUND!
            </CustomTypography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {paymentLinks!.map((link) => {
              const url =
                link.provider_link_short_url || link.provider_link_url || "";

              const handleCopy = async () => {
                if (!url) return;
                try {
                  await navigator.clipboard.writeText(url);
                  setCopiedId(link.id);
                  setTimeout(() => setCopiedId(null), 1500);
                } catch {
                  setCopiedId(null);
                }
              };

              return (
                <Box
                  key={link.id}
                  sx={{
                    border: "1px solid #E2E8F0",
                    borderLeft: `3px solid ${theme.palette.primary.main}`,
                    borderRadius: "10px",
                    overflow: "hidden",
                    backgroundColor: "#fff",
                  }}
                >
                  {/* Header row */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      px: 2,
                      py: 1.25,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: "0.72rem",
                        color: "#94A3B8",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      }}
                    >
                      #{link.id}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 100 }}>
                      <CustomTypography variant="body2" fontWeight={700}>
                        {link.provider}
                      </CustomTypography>
                      {link.reference_id && (
                        <CustomTypography variant="body3" color="#64748B">
                          Ref: {link.reference_id}
                        </CustomTypography>
                      )}
                    </Box>

                    {link.status && (
                      <Chip
                        label={link.status}
                        size="small"
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 600,
                          height: 22,
                          backgroundColor:
                            link.status.toLowerCase() === "paid"
                              ? "#DCFCE7"
                              : link.status.toLowerCase() === "expired"
                                ? "#FEE2E2"
                                : "#F1F5F9",
                          color:
                            link.status.toLowerCase() === "paid"
                              ? "#16A34A"
                              : link.status.toLowerCase() === "expired"
                                ? "#DC2626"
                                : "#64748B",
                          border: `1px solid ${
                            link.status.toLowerCase() === "paid"
                              ? "#BBF7D0"
                              : link.status.toLowerCase() === "expired"
                                ? "#FECACA"
                                : "#E2E8F0"
                          }`,
                        }}
                      />
                    )}

                    {link.created_by_admin_name && (
                      <CustomTypography
                        variant="body3"
                        color="#64748B"
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {link.created_by_admin_name}
                      </CustomTypography>
                    )}

                    {/* Refresh action */}
                    {canRefresh(link.last_manual_refresh_at) ? (
                      <Tooltip title="Refresh payment link">
                        <IconButton
                          size="small"
                          disabled={refreshingId === link.id}
                          onClick={async () => {
                            try {
                              setRefreshingId(link.id);
                              await onRefresh(link.id);
                            } catch (error) {
                              // eslint-disable-next-line no-console
                              console.error(
                                "Failed to refresh payment link",
                                error,
                              );
                            } finally {
                              setRefreshingId(null);
                            }
                          }}
                        >
                          {refreshingId === link.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <RefreshIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <CustomTypography variant="body3" color="text.secondary">
                        {link.last_manual_refresh_at
                          ? `Next at ${getNextRefreshTime(link.last_manual_refresh_at)}`
                          : "-"}
                      </CustomTypography>
                    )}
                  </Box>

                  {/* URL row */}
                  {url && (
                    <Box
                      sx={{
                        px: 2,
                        py: 0.75,
                        backgroundColor: "#F8FAFC",
                        borderTop: "1px solid #F1F5F9",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Tooltip title={url}>
                        <Box
                          sx={{
                            flex: 1,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontSize: "0.78rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          {url}
                        </Box>
                      </Tooltip>
                      <Tooltip title={copiedId === link.id ? "Copied" : "Copy"}>
                        <IconButton size="small" onClick={handleCopy}>
                          <CopyAllIcon
                            fontSize="small"
                            color={
                              copiedId === link.id ? "primary" : "inherit"
                            }
                          />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}

                  {/* Metrics strip */}
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      backgroundColor: "#FAFAFA",
                      borderTop: "1px solid #F1F5F9",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 3,
                    }}
                  >
                    {[
                      {
                        label: "Amount",
                        value: `${link.amount} ${link.currency.toUpperCase()}`,
                      },
                      {
                        label: "Percentage",
                        value: `${
                          link.percentage % 1 === 0
                            ? Math.floor(link.percentage)
                            : link.percentage
                        }%`,
                      },
                      {
                        label: "Updated At",
                        value: formatDate(link.updated_at) || "-",
                      },
                      {
                        label: "Created At",
                        value: formatDate(link.created_date) || "-",
                      },
                    ].map((m) => (
                      <Box
                        key={m.label}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.25,
                          minWidth: 68,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            fontSize: "0.63rem",
                            color: "#94A3B8",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {m.label}
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            fontSize: "0.82rem",
                            fontWeight: 600,
                            color: "#334155",
                          }}
                        >
                          {m.value}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default observer(OrderPaymentLinksTable);
